import {api, RequestParam} from "@/shared/api";
import {TaskDropdown} from "@/shared/task/taskDropdown";
import {TaskSelectionConfigurationComponent} from "./taskSelectionConfigurationComponent";
import {FormUtil} from "@/shared/forms/formUtil";
import * as Constants from "@/shared/constants";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Button from "@/shared/utils/button";
import * as Input from "@/shared/utils/input";
import {ErrorResponse, Task, TerminalConfig, WorkingTimeType} from "@/shared/timr";
import {MobileTaskSelectionType} from "@/shared/timrTypes";

export interface AddTerminalDialogConfig {
	minuteBasedWorkingTimeTypes: WorkingTimeType[];
}

export class AddTerminalDialog {

	public static EVENT_ADDED_TERMINAL = "event_added_terminal";
	public static EVENT_CLOSED = "event_closed";

	// duration in milliseconds
	public static QRCODE_FETCHING_USAGE_TIMEOUT = 1500; // timeout-duration between the api calls to check if the qr code it has been successfully scanned
	public static QRCODE_LOADING_DURATION = 1700; // duration for the loading icon, when the qr code has been successfully scanned
	public static FINISH_CLOSING_DURATION = 2000; // duration for the duration until the finish step is closed when the terminal has successfully connected

	private modal: BootstrapModal.Modal;
	private wizard: JQuery;
	private terminalId: number = null;
	private periodicalExecutor: number;
	private deleteTerminalConfigRequest: JQuery.jqXHR<void>;
	private isTerminalConfigNameValidRequest: JQuery.jqXHR<boolean>;
	private wizardResetted = false;
	private wizardFinished = false;
	private isReconnectWizard = false;
	private isTerminalQrCodeLoaded = false;
	private selectedTaskId: number;
	private selectedWorkingTimeTypeId: number;
	private taskDropdown: TaskDropdown;
	private _workingTimeTypeSelect: JQuery;
	private _terminalTaskSelectionConfigSelect: JQuery;
	private taskSelectionConfigurationComponent: TaskSelectionConfigurationComponent;

	constructor(public config: AddTerminalDialogConfig) {
		this.initModal();
		this.initWizard();
		this.resetFields();
		this.initListeners();
		this.initTaskDropdown();
		this.initWorkingTimeTypeSelect(config.minuteBasedWorkingTimeTypes);
	}

	public open(terminal: TerminalConfig) {
		this.isReconnectWizard = terminal != null;
		this.terminalId = null;

		// only initialize the taskSelection configuration if ProjectTime is enabled
		// document.querySelector will return null if ProjectTime is disabled (see addTerminalDialog.jsp)
		if (globals.projectTimeEnabled) {
			this.taskSelectionConfigurationComponent = document.querySelector("#addTerminalDialog #taskSelectionConfigurationComponent");
			this.taskSelectionConfigurationComponent.config = {
				availableTaskSelectionTypes: [MobileTaskSelectionType.ALL, MobileTaskSelectionType.BARCODE, MobileTaskSelectionType.LOCATION, MobileTaskSelectionType.RECOMMENDED_AND_RECENT],
				selectedTaskSelectionTypes: terminal?.taskSelectionTypes,
				openSearchInAll: terminal?.taskSelectionOpenSearchInAll ?? false,
				startOnSelection: terminal?.taskSelectionStartOnSelection ?? false,
				openAutomatically: terminal?.taskSelectionOpenAutomatically ?? false,
				visible: terminal && !terminal.useMobileTaskSelectionTypes
			};
			this.taskSelectionConfigurationComponent.defaultTaskSpecified = !!terminal?.defaultTask;
		}

		this.modal.show();
		// the input fields have to be propagated after the modal is opened,
		// because the input fields are not loaded until the modal is opened for the first time
		if (this.isReconnectWizard) {
			this.terminalId = terminal.id;
			this.isTerminalQrCodeLoaded = false;
			this.setFields(terminal);
			this.updateModalTitle();
			this.wizard.steps("setStep", 1);
		}
	}

	private initListeners() {
		this.wizard.find("#addTerminalDialogDeviceName").on("input", () => this.updateModalTitle());
		// if the user changes or reloads the page, but the wizard didn't complete successfully the new created TerminalAuthToken has to be deleted
		window.onbeforeunload = () => {
			this.stopPeriodicalTerminalAuthTokenUsageUpdate();
			if (this.terminalQrCodeLoaded() && !this.wizardFinished) {
				this.deleteTerminalAuthTokenQrCode(!this.isReconnectWizard);
			}
		};
		this.wizard.submit(e => e.preventDefault());
	}

	private initModal() {
		const modalId = $("#addTerminalDialog");
		this.modal = new BootstrapModal.Modal({
			title: this.isReconnectWizard ? "terminals.wizard.header.reconnect" : "terminals.wizard.header.add",
			message: modalId,
			size: "modal-lg",
			footer: false,
			css: {
				content: "test-css"
			},
			onClose: () => {
				this.resetWizard();
				this.resetFields();
				this.updateModalTitle();
				this.stopPeriodicalTerminalAuthTokenUsageUpdate();
				/*
				 * the new terminal must not be deleted when it is has successfully finished
				 * it may happen that it has already been deleted via cancelButton-click or stepping to the first page
				 */
				if (this.terminalQrCodeLoaded() && this.wizardResetted && !this.wizardFinished) {
					this.deleteTerminalAuthTokenQrCode(!this.isReconnectWizard);
				}
				if (this.isReconnectWizard) {
					$(this).trigger(AddTerminalDialog.EVENT_CLOSED);
				}
				if (this.wizardFinished) {
					$(this).triggerHandler(AddTerminalDialog.EVENT_ADDED_TERMINAL);
				}
			},
			onOpen: () => {
				this.wizardResetted = false;
				this.wizardFinished = false;
				if (!this.isReconnectWizard) {
					this.wizard.find("#addTerminalDialogDeviceName").focus();
					this.deleteTerminalAuthTokenQrCode(false);
				}
			}
		});
		modalId.find("button[data-dismiss=modal]").click(() => this.resetWizard());
	}

	private initWizard() {
		this.wizard = $("#addTerminalDialog > form").steps({
			headerTag: "h6",
			bodyTag: "fieldset",
			transitionEffect: "fade",
			cssClass: "wizard modal-wizard",
			enableCancelButton: true,
			enableFinishButton: false,
			enableKeyNavigation: false,
			labels: {
				previous: messages["steps.previous"],
				next: messages["steps.next"],
				finish: messages["dialog.finish"],
				cancel: messages["dialog.cancel"]
			},
			onStepChanged: (event: Event, currentIndex: number, priorIndex: number) => {
				const form = $(event.target);
				this.resetButtonsStateOnStepChanged();

				if (currentIndex === 0) {
					form.find("a[href='#next']").show();
					form.find("a[href='#previous']").closest("li")
						.addClass("disabled")
						.prop("aria-disabled", "true");
				}
				if (currentIndex === 1 && !this.wizardFinished) {
					this.startPeriodicalTerminalAuthTokenUsageUpdate();
				}
				if (currentIndex === 2) {
					this.isTerminalQrCodeLoaded = false;
					this.wizardFinished = true;
					form.find("a[href='#cancel']").hide();
					setTimeout(() => {
						this.modal.close();
						this.resetWizard();
					}, AddTerminalDialog.FINISH_CLOSING_DURATION);
					this.setTerminalAuthTokenQrCodeLoadingState(false);
				}
			},
			onFinishing: () => {
				this.modal.close();
			},
			onStepChanging: async (event: Event, currentIndex: number, newIndex: number) => {
				// isAsyncStep is for a workaround for async validation request
				// https://stackoverflow.com/a/51191180
				const form = $(event.target);
				const nextButton = form.find("a[href='#next']");
				const previousButton = form.find("a[href='#previous']").show();
				const cancelButton = form.find("a[href='#cancel']").show();
				this.setButtonsStateOnStepChange(true);
				const nextStep = currentIndex < newIndex;
				this.setButtonsStateOnStepChange(nextStep);
				if (nextStep) {
					if (currentIndex === 0) {
						if (await this.generateTerminalAuthTokenQrCode()) {
							nextButton.hide();
							this.setButtonsStateOnStepChange(true);
							return true;
						} else {
							return false;
						}
					} else if (currentIndex === 1) {
						/*
						 * the terminal has successfully connected
						 * step backwards and cancel must be invisible and the steps state indicator be disabled
						 */
						this.wizard.steps("enableAria", false);
						previousButton.hide();
						cancelButton.hide();
						return true;
					} else {
						return true;
					}
					/*
					 * when the wizard get closed it has to be resetted with wizard.steps("reset").
					 * this method triggers the onStepChanging/onStepChanged event and would step into this branch (because of newIndex === 0).
					 * the wizardResetted variable is used to avoid this (duplicated delete API calls)
					 */
				} else {
					return true;
				}
			},
			onInit: (event: Event) => {
				// initialize custom input components in wizard
				const form = $(event.target);
				const nextButton = form.find("a[href='#next']").off();
				const previousButton = form.find("a[href='#previous']").off();
				// DEVNOTE: Under this comment is a workaround for a bug in jquery.steps.js -> Don't mark steps as error
				// Probably to do because of async behaviour
				nextButton.on("click", async (e) => {
					e.preventDefault();
					if (await this.isTerminalConfigNameValid()) {
						this.wizard.steps("next");
					}
				});
				previousButton.on("click", (e) => {
					e.preventDefault();
					this.stopPeriodicalTerminalAuthTokenUsageUpdate();
					this.deleteTerminalAuthTokenQrCode(!this.isReconnectWizard);
					this.wizard.steps("previous");
				});
			},
			onCanceled: () => {
				this.stopPeriodicalTerminalAuthTokenUsageUpdate();
				this.deleteTerminalAuthTokenQrCode(!this.isReconnectWizard);
				this.resetWizard();
				this.resetFields();
				this.modal.close();
			}
		});
	}

	private updateModalTitle() {
		let message = this.isReconnectWizard ?
			messages["terminals.wizard.header.reconnect"] : messages["terminals.wizard.header.add"];

		const deviceName = this.wizard.find("#addTerminalDialogDeviceName").val();
		if (deviceName) {
			message += ": " + (deviceName as string);
		}

		this.modal.setTitle(message, true);
	}

	private async isTerminalConfigNameValid(): Promise<boolean> {
		const form = this.wizard.serializeArray();

		this.isTerminalConfigNameValidRequest?.abort();
		try {
			this.isTerminalConfigNameValidRequest = api("admin/isTerminalConfigNameValid.json").get(form);
			const valid: boolean = await this.isTerminalConfigNameValidRequest;
			FormUtil.removeAllFeedback(this.wizard);
			return valid;
		} catch (response: unknown) {
			this.resetButtonsStateOnStepChanged();
			const errorResponse = (response as ErrorResponse).responseJSON;
			if (errorResponse?.fieldErrors != null) {
				FormUtil.markFieldErrors(this.wizard, errorResponse.fieldErrors);
			}
			return false;
		}
	}


	private resetButtonsStateOnStepChanged() {
		const previousButton = this.wizard.find("a[href='#previous']");
		const nextButton = this.wizard.find("a[href='#next']");

		Button.resetLoading(nextButton);
		Button.resetLoading(previousButton);
		this.setWizardButtonState(false);
		Input.enable(this.modal.find("button[data-dismiss=modal]"));
	}

	private setButtonsStateOnStepChange(nextStep: boolean) {
		const previousButton = this.wizard.find("a[href='#previous']");
		const nextButton = this.wizard.find("a[href='#next']");

		if (nextStep) {
			Button.setLoading(nextButton);
		} else {
			Button.setLoading(previousButton);
		}
		this.setWizardButtonState(true);
		Input.disable(this.modal.find("button[data-dismiss=modal]"));
	}

	private async generateTerminalAuthTokenQrCode(): Promise<{ success: boolean; error?: unknown }> {
		if (this.terminalQrCodeLoaded()) {
			return {success: true};
		} else {
			const formData: RequestParam[] = this.wizard.serializeArray();

			if (this.wizard.find("select[name=useMobileTaskSelectionTypes]").val() === "false") {
				// add Task Selection Configuration to FormData
				const {
					taskSelectionTypes,
					startOnSelection,
					openSearchInAll,
					openAutomatically
				} = this.taskSelectionConfigurationComponent.getData();
				formData.push({name: "taskSelectionTypes", value: taskSelectionTypes.join()});
				formData.push({name: "taskSelectionStartOnSelection", value: startOnSelection});
				formData.push({name: "taskSelectionOpenSearchInAll", value: openSearchInAll});
				formData.push({name: "taskSelectionOpenAutomatically", value: openAutomatically});
			}

			const id: number = await api("admin/terminalConfigWithAuthToken.json").post(formData);
			this.terminalId = id;
			return new Promise((resolve) => {
				const img = document.createElement("img");
				img.src = "/timr/admin/terminalConfigWithAuthTokenQRCode.png?terminalId=" + id + "&_=" + new Date().getTime();
				img.width = Constants.QR_CODE_SIDELENGTH;
				img.height = Constants.QR_CODE_SIDELENGTH;
				this.wizard.find("#addTerminalDialogQrCode").append(img);
				this.isTerminalQrCodeLoaded = true;
				img.onload = () => {
					resolve({success: true});
				};
			});
		}
	}


	private startPeriodicalTerminalAuthTokenUsageUpdate() {
		this.stopPeriodicalTerminalAuthTokenUsageUpdate();
		if (this.terminalId != null) {
			this.periodicalExecutor = setInterval(() => this.fetchTerminalAuthTokenUsage(), AddTerminalDialog.QRCODE_FETCHING_USAGE_TIMEOUT);
		}
	}

	private stopPeriodicalTerminalAuthTokenUsageUpdate() {
		if (this.periodicalExecutor) {
			clearInterval(this.periodicalExecutor);
			this.periodicalExecutor = null;
		}
	}

	private fetchTerminalAuthTokenUsage() {
		api("admin/isTerminalConfigAuthTokenUsed.json")
			.get({
				terminalId: this.terminalId
			})
			.done((terminalAuthTokenUsed: boolean) => {
				if (terminalAuthTokenUsed) {
					this.terminalAuthTokenUsed();
				}
			});
	}

	private terminalAuthTokenUsed() {
		this.stopPeriodicalTerminalAuthTokenUsageUpdate();
		setTimeout(() => this.wizard.steps("next"), AddTerminalDialog.QRCODE_LOADING_DURATION);

		this.setTerminalAuthTokenQrCodeLoadingState(true);
		this.setWizardButtonState(true);
		Button.setLoading(this.wizard.find("a[href='#next']"));
	}

	private deleteTerminalAuthTokenQrCode(inclusiveApiRequest = true): JQueryPromise<boolean> {
		const deferred: JQueryDeferred<boolean> = $.Deferred();
		this.wizard.find("#addTerminalDialogQrCode > img").remove();
		if (this.terminalQrCodeLoaded() && !this.isReconnectWizard) {
			if (inclusiveApiRequest) {
				this.deleteTerminalConfigRequest?.abort();
				this.deleteTerminalConfigRequest = api("admin/deleteTerminalConfig.json")
					.post({
						terminalId: this.terminalId
					})
					.done(() => {
						deferred.resolve(true);
						this.terminalId = null;
						this.isTerminalQrCodeLoaded = false;
					})
					.fail(
						() => deferred.reject()
					);
				return deferred.promise();
			}
		} else {
			this.isTerminalQrCodeLoaded = false;
			return deferred.resolve(true).promise();
		}
	}

	private setTerminalAuthTokenQrCodeLoadingState(loading: boolean) {
		this.wizard.find("#addTerminalDialogQrCodeStep").toggleClass("qrCodeLoading", loading);
	}

	private resetWizard() {
		FormUtil.removeAllFeedback(this.wizard);
		this.wizardResetted = true;
		this.wizard.steps("reset");
	}

	private resetFields() {
		this.wizard.find("#addTerminalDialogDeviceName").val("");
		this.wizard.find("#addTerminalDialogTimeoutDuration").val("15000");
		this.wizard.find("#addTerminalQrCodeAuthentication").prop("checked", true);
		this.wizard.find("#useDefaultLocationRequirements").val("false");
		this.wizard.find("#geofenceEnabled").val("false");
		this.wizard.find("#addTerminalDialogTerminalId").val(null);
		this.wizard.find("#addTerminalDialogQrCode > img").remove();
		this.wizard.find("#defaultTask.id").val(null);
		this.taskDropdown?.setSelectedTask(null);
		this.workingTimeTypeSelect.prop('selectedIndex', 0);

	}

	private setFields(terminal: TerminalConfig) {
		this.wizard.find("#addTerminalDialogDeviceName").val(terminal.name);
		this.wizard.find("#addTerminalDialogTimeoutDuration").val(terminal.timeoutDuration);
		this.wizard.find("#addTerminalQrCodeAuthentication").prop("checked", terminal.qrCodeAuthentication);
		this.wizard.find("#useDefaultLocationRequirements").val(terminal.useDefaultLocationRequirements.toString());
		this.wizard.find("#geofenceEnabled").val(terminal.geofenceEnabled.toString());
		this.wizard.find("#addTerminalDialogTerminalId").val(terminal.id);
		this.wizard.find("#defaultTask.id").val(terminal.defaultTask?.id);
		this.wizard.find("#taskSelectionConfig").val(terminal.useMobileTaskSelectionTypes.toString());

		const defaultWorkingTimeType = terminal.defaultWorkingTimeType;
		if (defaultWorkingTimeType) {
			this.workingTimeTypeSelect.val(defaultWorkingTimeType.id)
		} else {
			this.workingTimeTypeSelect.prop('selectedIndex', 0);
		}

		if (terminal.defaultTask) {
			this.taskDropdown.setSelectedTask(terminal.defaultTask);
		}

	}

	private terminalQrCodeLoaded(): boolean {
		return this.isTerminalQrCodeLoaded;
	}

	private setWizardButtonState(setDisabled: boolean) {
		const liElement = this.wizard.find("ul[role=menu] > li");
		const aElement = liElement.find("> a");
		const dataDismiss = this.modal.find("button[data-dismiss=modal]");

		liElement.toggleClass("disabled", setDisabled);
		liElement.prop("aria-disabled", String(setDisabled));
		aElement.toggleClass("disabled", setDisabled);
		if (setDisabled) {
			Input.disable(dataDismiss);
		} else {
			Input.enable(dataDismiss);
		}
	}

	private initTaskDropdown() {
		this.taskDropdown = new TaskDropdown({
			rootElement: $("#terminalDefaultTaskDropdownAdd"),
			allowSelectionOfNotBookable: false,
			hideInactive: true,
			emptyMessage: messages["terminals.dialog.option.none"]
		}, {
			loadRootNodesURL: "tasks/loadAllTasks.json",
			loadSubNodesURL: "tasks/taskTreeLoad.json",
			filterNodesURL: "tasks/filterTasks.json"
		});

		$(this.taskDropdown).on(TaskDropdown.EVENT_SELECTED, (_, task: Task) => {
			this.taskSelectionConfigurationComponent.defaultTaskSpecified = !!task;
		});
	}

	private initWorkingTimeTypeSelect(workingTimeTypes: WorkingTimeType[]) {
		this.workingTimeTypeSelect.empty();

		this.workingTimeTypeSelect.append($("<option></option>", {value: "null"}).text(messages["terminals.dialog.option.none"]));
		workingTimeTypes.forEach(wtt => {
			this.workingTimeTypeSelect.append($("<option></option>", {value: wtt.id}).text(wtt.name));
		});

		this.workingTimeTypeSelect.on("change", () => {
			const selectValue: string = <string>this.workingTimeTypeSelect.val();
			this.modal.getDomModal().find("input[name='defaultWorkingTimeType\\.id']").val(selectValue === "null" ? null : selectValue);
		});

		this.terminalTaskSelectionConfigSelect.on("change", () => {
			this.taskSelectionConfigurationComponent.changeVisibility(this.terminalTaskSelectionConfigSelect.val() === "false");
		})
	}

	private get workingTimeTypeSelect(): JQuery {
		if (!this._workingTimeTypeSelect) {
			this._workingTimeTypeSelect = $("#addTerminalDialog #defaultWorkingTimeType");
		}
		return this._workingTimeTypeSelect;
	}

	private get terminalTaskSelectionConfigSelect(): JQuery {
		if (!this._terminalTaskSelectionConfigSelect) {
			this._terminalTaskSelectionConfigSelect = $("#addTerminalDialog #taskSelectionConfig");
		}
		return this._terminalTaskSelectionConfigSelect;
	}
}
